import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import NowPlayingModal from './NowPlayingModal';
import './Phone3D.css';

// Canvas is taller than the visible screen window so the card can scroll.
// Screen matches the reference screenshot's aspect (407:882 == 540:1170).
const CW = 540; // canvas / screen width
const WINDOW_CH = 1170; // visible screen height
const FULL_CH = 1400; // taller than the window so the lower panels have room + scroll

const LINKS = {
  twitter: 'https://twitter.com/Quinnscodes',
  github: 'https://github.com/quinnlicious',
  linkedin: 'https://www.linkedin.com/in/akquinn/',
};

// SVG path data (24x24 viewBox) reused from the contact card icons.
const ICONS = {
  twitter: 'M12 3C6.5 3 2 6.4 2 10.6c0 2 1 3.8 2.6 5.2-.1 1.1-.6 2.2-1.3 3-.2.3 0 .7.4.6 1.7-.3 3-1 3.9-1.6 1.3.4 2.7.6 4.4.6 5.5 0 10-3.4 10-7.8S17.5 3 12 3Z',
  github: 'M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.3 1l-2.2 2.2Z',
  linkedin: 'M17 10.5V7c0-.6-.4-1-1-1H4c-.6 0-1 .4-1 1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1v-3.5l4 4v-11l-4 4Z',
  email: 'M2 6c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6Zm2 .25v.2l8 4.8 8-4.8v-.2H4Zm16 2.5-8 4.8-8-4.8V18h16V8.75Z',
};

const BUTTON_ORDER = ['twitter', 'github', 'linkedin', 'spotify'];

// Spotify "sound wave" strokes (24x24), matching the contact-card icon
const SPOTIFY_WAVES = [
  'M5 8c5-1.4 10-0.6 13.5 1.8',
  'M5.5 11.6c4.4-1.1 8.6-0.5 12 1.6',
  'M6.2 15.2c3.4-0.9 6.4-0.6 9.3 1',
];

const Phone3D = () => {
  const mountRef = useRef(null);
  const hotspotsRef = useRef([]); // [{ cx, cy, r, href|action }] in full-canvas coords
  const screenMeshRef = useRef(null); // the model's actual screen mesh
  const [npOpen, setNpOpen] = useState(false);
  const openNpRef = useRef(null);
  openNpRef.current = setNpOpen;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
    camera.position.set(0, 0, 12);

    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.85);
    keyLight.position.set(5, 8, 6);
    scene.add(keyLight);
    const rim = new THREE.DirectionalLight(0x88aaff, 0.45);
    rim.position.set(-6, -2, 4);
    scene.add(rim);

    const phone = new THREE.Group();
    scene.add(phone);

    // Environment for realistic metal/glass reflections on the model
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;

    // --- Scrollable contact-card canvas (drawn below) ---
    const canvas = document.createElement('canvas');
    canvas.width = CW;
    canvas.height = FULL_CH;
    const ctx = canvas.getContext('2d');
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
    const repeatY = WINDOW_CH / FULL_CH;
    texture.repeat.set(1, repeatY);
    const maxOffset = 1 - repeatY;
    texture.offset.set(0, maxOffset); // show the top of the card

    // Rounded-corner alpha mask for the card plane (matches the screen corners)
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = CW;
    maskCanvas.height = WINDOW_CH;
    const mctx = maskCanvas.getContext('2d');
    mctx.fillStyle = '#fff';
    mctx.beginPath();
    mctx.roundRect(0, 0, CW, WINDOW_CH, 60);
    mctx.fill();
    const maskTex = new THREE.CanvasTexture(maskCanvas);

    // Card plane — positioned over the model's screen once it loads
    const screen = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: texture, alphaMap: maskTex, transparent: true, toneMapped: false })
    );
    screen.visible = false;
    phone.add(screen);

    // --- Load the iPhone 17 model and lay the card over its screen ---
    const loader = new GLTFLoader();
    loader.load(
      `${process.env.PUBLIC_URL}/models/iphone17.glb`,
      (gltf) => {
        const model = gltf.scene;
        phone.add(model);

        // Scale to a target height, then recenter at the origin
        let box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        model.scale.setScalar(6.4 / size.y);
        phone.updateMatrixWorld(true);
        box = new THREE.Box3().setFromObject(model);
        model.position.sub(box.getCenter(new THREE.Vector3()));
        phone.updateMatrixWorld(true);

        // Hide the model's own screen + glass; find the display mesh
        let screenMesh = null;
        model.traverse((o) => {
          if (!o.isMesh || !o.material) return;
          const n = o.material.name;
          if (n === 'Screen_BG') screenMesh = o;
          if (n === 'Screen_BG' || n === 'Screen_Glass') o.visible = false;
        });
        screenMeshRef.current = screenMesh;

        // Size/position the card plane over the screen
        const INSET = 0.975;
        const sbox = new THREE.Box3().setFromObject(screenMesh || model);
        const sCenter = sbox.getCenter(new THREE.Vector3());
        const sSize = sbox.getSize(new THREE.Vector3());
        screen.scale.set((sSize.x || 2.6) * INSET, (sSize.y || 5.6) * INSET, 1);
        // Sit just in front of the screen surface (center of the thin screen mesh),
        // not its bounding-box front — otherwise it floats off as the phone turns.
        screen.position.set(sCenter.x, sCenter.y, sCenter.z + 0.004);
        screen.visible = true;
      },
      undefined,
      (err) => console.error('iPhone model failed to load', err)
    );

    // --- Draw the (tall) contact card onto the canvas ---
    const photo = new Image();
    photo.src = `${process.env.PUBLIC_URL}/images/quinn-hero.webp`;

    const drawCover = (img, x, y, w, h) => {
      const tr = w / h;
      let sy = img.height * 0.28; // start lower so the head sits near the top
      let sh = img.height - sy;
      let sw = sh * tr;
      if (sw > img.width) {
        sw = img.width;
        sh = sw / tr;
        sy = Math.min(sy, img.height - sh);
      }
      const sx = (img.width - sw) / 2;
      ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
    };

    const drawIcon = (k, cx, cy, size) => {
      const p = new Path2D(ICONS[k]);
      ctx.save();
      ctx.translate(cx - size / 2, cy - size / 2);
      ctx.scale(size / 24, size / 24);
      ctx.fillStyle = '#fff';
      ctx.fill(p);
      ctx.restore();
    };

    const panel = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, r);
      ctx.fillStyle = 'rgba(28,28,30,0.92)'; // iOS dark list cell
      ctx.fill();
    };

    const chevron = (cx, cy) => {
      ctx.strokeStyle = 'rgba(235,235,245,0.4)';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(cx - 5, cy - 9);
      ctx.lineTo(cx + 5, cy);
      ctx.lineTo(cx - 5, cy + 9);
      ctx.stroke();
    };

    const avatar = (cx, cy, r) => {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();
      if (photo.complete && photo.naturalWidth) drawCover(photo, cx - r, cy - r, r * 2, r * 2);
      else {
        ctx.fillStyle = '#333';
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      }
      ctx.restore();
    };

    const statusIcons = () => {
      ctx.fillStyle = '#fff';
      // cellular bars
      const bx = CW - 150;
      [10, 16, 22, 28].forEach((bh, i) => {
        ctx.fillRect(bx + i * 12, 50 - bh, 8, bh);
      });
      // wifi (three stacked arcs)
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      const wx = CW - 92;
      [20, 13, 6].forEach((rad) => {
        ctx.beginPath();
        ctx.arc(wx, 46, rad, Math.PI * 1.25, Math.PI * 1.75);
        ctx.stroke();
      });
      ctx.beginPath();
      ctx.arc(wx, 46, 1.5, 0, Math.PI * 2);
      ctx.fill();
      // battery
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.roundRect(CW - 62, 30, 44, 22, 5);
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.roundRect(CW - 58, 34, 32, 14, 2);
      ctx.fill();
      ctx.beginPath();
      ctx.roundRect(CW - 15, 36, 4, 10, 2);
      ctx.fill();
    };

    const infoRow = (label, value, y, isLink) => {
      ctx.textAlign = 'left';
      ctx.fillStyle = '#8e8e93';
      ctx.font = '22px -apple-system, "Segoe UI", sans-serif';
      ctx.fillText(label, 40, y);
      ctx.fillStyle = isLink ? '#0a84ff' : '#fff';
      ctx.font = '30px -apple-system, "Segoe UI", sans-serif';
      ctx.fillText(value, 40, y + 36);
    };

    // Trim text with an ellipsis so it never overflows the panel
    const fitText = (s, maxW, font) => {
      ctx.font = font;
      if (ctx.measureText(s).width <= maxW) return s;
      let str = s;
      while (str.length > 1 && ctx.measureText(str + '…').width > maxW) str = str.slice(0, -1);
      return str + '…';
    };

    // Latest Last.fm track (filled in asynchronously)
    const np = { text: '' };

    const drawScreen = () => {
      ctx.clearRect(0, 0, CW, FULL_CH);
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CW, FULL_CH);

      // Photo fills the top ~55% and fades into the dark UI
      const photoH = 640;
      if (photo.complete && photo.naturalWidth) {
        drawCover(photo, 0, 0, CW, photoH);
      } else {
        ctx.fillStyle = '#1c1c1e';
        ctx.fillRect(0, 0, CW, photoH);
      }
      const grad = ctx.createLinearGradient(0, 360, 0, photoH);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, '#000');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 360, CW, photoH - 360);

      // Status bar
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'left';
      ctx.font = '600 30px -apple-system, "Segoe UI", sans-serif';
      ctx.fillText('15:38', 34, 50);
      statusIcons();

      // Dynamic Island (iPhone 17)
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.roundRect(CW / 2 - 80, 16, 160, 42, 21);
      ctx.fill();

      // Back chrome + Edit
      ctx.fillStyle = 'rgba(50,50,55,0.45)';
      ctx.beginPath();
      ctx.arc(56, 86, 23, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(62, 75);
      ctx.lineTo(49, 86);
      ctx.lineTo(62, 97);
      ctx.stroke();
      ctx.fillStyle = 'rgba(50,50,55,0.45)';
      ctx.beginPath();
      ctx.roundRect(CW - 106, 66, 88, 40, 20);
      ctx.fill();
      ctx.fillStyle = '#e6e6e8';
      ctx.textAlign = 'center';
      ctx.font = '28px -apple-system, "Segoe UI", sans-serif';
      ctx.fillText('Edit', CW - 62, 96);

      // Name
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.font = '700 66px -apple-system, "Segoe UI", sans-serif';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 16;
      ctx.fillText('dr quinn', CW / 2, 448);
      ctx.shadowBlur = 0;

      // Action buttons — dark circles like the screenshot
      const r = 35;
      const gap = 119;
      const startX = CW / 2 - gap * 1.5;
      const by = 501;
      const hotspots = [];
      BUTTON_ORDER.forEach((k, i) => {
        const cx = startX + gap * i;
        if (k === 'spotify') {
          // green circle + white sound waves; opens the now-playing box
          ctx.beginPath();
          ctx.arc(cx, by, r, 0, Math.PI * 2);
          ctx.fillStyle = '#1db954';
          ctx.fill();
          ctx.save();
          ctx.translate(cx - 16.5, by - 16.5);
          ctx.scale(33 / 24, 33 / 24);
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          SPOTIFY_WAVES.forEach((d) => ctx.stroke(new Path2D(d)));
          ctx.restore();
          hotspots.push({ cx, cy: by, r: r * 1.3, action: 'nowplaying' });
        } else {
          ctx.beginPath();
          ctx.arc(cx, by, r, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(44,44,46,0.92)';
          ctx.fill();
          drawIcon(k, cx, by, 33);
          hotspots.push({ cx, cy: by, r: r * 1.3, href: LINKS[k] });
        }
      });
      hotspotsRef.current = hotspots;

      // Contact Photo & Poster row
      panel(18, 552, CW - 36, 78, 18);
      avatar(64, 591, 26);
      ctx.textAlign = 'left';
      ctx.fillStyle = '#fff';
      ctx.font = '26px -apple-system, "Segoe UI", sans-serif';
      ctx.fillText('Contact Photo & Poster', 106, 600);
      chevron(CW - 40, 591);

      // Grouped info panel: education, education, linkedin
      const gTop = 654;
      const rowH = 96;
      panel(18, gTop, CW - 36, rowH * 3, 18);
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      infoRow('education', 'PhD in VR in Education', gTop + 52);
      ctx.beginPath();
      ctx.moveTo(40, gTop + rowH);
      ctx.lineTo(CW - 18, gTop + rowH);
      ctx.stroke();
      infoRow('education', 'BSc in Computer Science', gTop + rowH + 52);
      ctx.beginPath();
      ctx.moveTo(40, gTop + rowH * 2);
      ctx.lineTo(CW - 18, gTop + rowH * 2);
      ctx.stroke();
      infoRow('other', 'linkedin.com/in/akquinn', gTop + rowH * 2 + 52, true);
      chevron(CW - 40, gTop + rowH * 2 + 42);

      // Notes
      const nTop = gTop + rowH * 3 + 32;
      panel(18, nTop, CW - 36, 168, 18);
      ctx.textAlign = 'left';
      ctx.fillStyle = '#8e8e93';
      ctx.font = '22px -apple-system, "Segoe UI", sans-serif';
      ctx.fillText('Notes', 40, nTop + 42);
      ctx.fillStyle = '#fff';
      ctx.font = '24px -apple-system, "Segoe UI", sans-serif';
      ctx.fillText('Software Developer at Ionology —', 40, nTop + 80);
      ctx.fillText('AI integration & development.', 40, nTop + 112);

      // Now playing (Last.fm) — under Notes
      const npTop = nTop + 168 + 28;
      panel(18, npTop, CW - 36, 110, 18);
      ctx.beginPath();
      ctx.arc(56, npTop + 55, 24, 0, Math.PI * 2);
      ctx.fillStyle = '#1db954';
      ctx.fill();
      ctx.save();
      ctx.translate(56 - 15, npTop + 55 - 15);
      ctx.scale(30 / 24, 30 / 24);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      SPOTIFY_WAVES.forEach((d) => ctx.stroke(new Path2D(d)));
      ctx.restore();
      ctx.textAlign = 'left';
      ctx.fillStyle = '#8e8e93';
      ctx.font = '22px -apple-system, "Segoe UI", sans-serif';
      ctx.fillText('Now playing', 100, npTop + 46);
      ctx.fillStyle = '#fff';
      const npFont = '26px -apple-system, "Segoe UI", sans-serif';
      ctx.font = npFont;
      ctx.fillText(fitText(np.text, CW - 130, npFont), 100, npTop + 82);

      // Home indicator
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.beginPath();
      ctx.roundRect(CW / 2 - 70, FULL_CH - 22, 140, 6, 3);
      ctx.fill();

      texture.needsUpdate = true;
    };

    drawScreen();
    photo.onload = drawScreen;

    // Fetch the current Last.fm track for the "Now playing" row
    const apiKey = process.env.REACT_APP_LASTFM_API_KEY;
    const fetchNowPlaying = async () => {
      if (!apiKey) return;
      try {
        const u = `https://ws.audioscrobbler.com/2.0/?method=user.getrecenttracks&user=xoialexiox&api_key=${apiKey}&limit=1&format=json`;
        const res = await fetch(u);
        const data = await res.json();
        const t = data?.recenttracks?.track;
        const tr = Array.isArray(t) ? t[0] : t;
        if (tr) {
          const name = tr.name || '';
          const artist = tr.artist?.['#text'] || '';
          np.text = artist ? `${name} — ${artist}` : name;
          drawScreen();
        }
      } catch (e) {
        /* ignore */
      }
    };
    fetchNowPlaying();
    const npTimer = setInterval(fetchNowPlaying, 30000);

    // --- Controls: bounded rotation, screen never turns away ---
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = false; // auto-spin is driven manually (ping-pong sway)
    controls.minAzimuthAngle = -0.5;
    controls.maxAzimuthAngle = 0.5;
    controls.minPolarAngle = Math.PI * 0.24;
    controls.maxPolarAngle = Math.PI * 0.78;

    // --- Scroll the screen content via the mouse wheel ---
    const onWheel = (e) => {
      e.preventDefault();
      const next = THREE.MathUtils.clamp(texture.offset.y - e.deltaY * 0.0009, 0, maxOffset);
      texture.offset.y = next;
    };
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

    // --- Click → hotspot raycasting (accounting for scroll offset) ---
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let downX = 0;
    let downY = 0;
    const onPointerDown = (e) => {
      downX = e.clientX;
      downY = e.clientY;
    };
    const onPointerUp = (e) => {
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > 6) return; // a drag
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObject(screen);
      if (!hits.length || !hits[0].uv) return;
      const ty = hits[0].uv.y * repeatY + texture.offset.y; // texture-space (0 bottom)
      const px = hits[0].uv.x * CW;
      const py = (1 - ty) * FULL_CH; // canvas pixel (top origin)
      const hit = hotspotsRef.current.find((h) => Math.hypot(px - h.cx, py - h.cy) <= h.r);
      if (!hit) return;
      if (hit.action === 'nowplaying') openNpRef.current?.(true);
      else if (hit.href) window.open(hit.href, '_blank', 'noopener,noreferrer');
    };
    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointerup', onPointerUp);

    let raf;
    const animate = (now = 0) => {
      raf = requestAnimationFrame(animate);
      // Gentle ping-pong sway — a slight left/right movement, repeats forever
      phone.rotation.y = 0.16 * Math.sin(now * 0.0005);
      controls.update();
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(animate);

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(npTimer);
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointerup', onPointerUp);
      controls.dispose();
      texture.dispose();
      maskTex.dispose();
      screen.geometry.dispose();
      screen.material.dispose();
      pmrem.dispose();
      scene.traverse((o) => {
        if (o.isMesh) {
          o.geometry?.dispose();
          if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
          else o.material?.dispose();
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="phone3d">
      <div className="phone3d-canvas" ref={mountRef} />
      <div className="phone3d-hint">Drag to rotate · scroll the screen · tap a button</div>
      <NowPlayingModal open={npOpen} onClose={() => setNpOpen(false)} />
    </div>
  );
};

export default Phone3D;
